import type { RecurrenceRule, Task } from '../db/models'
import { startOfDay } from '../date/calendar'
import { fixedOccurrenceOnOrAfter } from './nextOccurrence'

type StartPatch = Partial<Pick<Task, 'startDate' | 'startTime' | 'startPeriod'>>

/** Was das Aufwecken liest — auch eine Zeile (`TaskEntryData`) trägt das. */
export interface WakeSource {
  recurrence?: RecurrenceRule | null
  startDate?: Date | null
}

/**
 * Der Termin, den eine Aufgabe beim Setzen ihrer Wiederholung bekommt.
 *
 * **Die Regel setzt das Wann neu** (Nutzer-Entscheidung 21.09.2026) — auch
 * wenn dort schon eines stand. Bis dahin blieb ein gesetztes Datum liegen und
 * erst der **Nachfolger** richtete sich nach der Regel; das ließ zwei Termine
 * nebeneinander stehen, den eigenen und den der Regel, und war niemandem zu
 * erklären. Wer eine Wiederholung setzt, meint sie ab sofort.
 *
 * Nur ein **fester Rhythmus** liefert einen Termin: Bei der **Quote**
 * entscheidet der Nutzer die Tage selbst, und **nach Abhaken** gibt es vor dem
 * ersten Haken nichts zu rechnen. `null` = nichts zu setzen.
 */
export function firstStartForRule(rule: RecurrenceRule, today: Date): Date | null {
  if (rule.type !== 'fixed') return null
  const day = startOfDay(today)
  return fixedOccurrenceOnOrAfter(rule, day, day)
}

/**
 * **Die Regel setzt das Wann** (Nutzer-Entscheidung 21.09.2026, CONTEXT.md Wiederholung) — auch ein
 * schon gesetztes: Wer eine Wiederholung wählt, meint sie ab sofort. Die Änderung für eine Aufgabe,
 * die `rule` bekommt; ohne Beginn (Quote, nach Abhaken, `null`) nur die Regel.
 */
export function recurrenceUpdate(rule: RecurrenceRule | null, today: Date):
  { recurrence: RecurrenceRule | null } & Partial<{ startDate: Date; startTime: null; startPeriod: null }> {
  const first = rule ? firstStartForRule(rule, today) : null
  return first ? { recurrence: rule, startDate: first, startTime: null, startPeriod: null } : { recurrence: rule }
}

/**
 * **Aufwecken holt den Termin nach vorn** (CONTEXT.md, Wiederholung; entschieden 2026-09-13).
 * Wacht eine wiederkehrende Aufgabe OHNE neues Datum auf und liegt ihr Termin vor heute, steht
 * sie am nächsten Termin ab heute: fester Rhythmus laut Regel (Phase am alten Termin), **Quote**
 * heute. **Nach Abhaken** behält sein Datum — dort ist nichts verschlafen. Leeres Objekt = nichts
 * zu ändern.
 */
export function wakeStart(
  task: WakeSource,
  today: Date,
): StartPatch {
  const rule = task.recurrence
  if (!rule || !task.startDate) return {}
  const day = startOfDay(today)
  if (startOfDay(task.startDate) >= day) return {}
  switch (rule.type) {
    case 'after_done':
      return {}
    case 'quota':
      return { startDate: day, startTime: null, startPeriod: null }
    case 'fixed': {
      const next = fixedOccurrenceOnOrAfter(rule, day, task.startDate)
      return next ? { startDate: next, startTime: null, startPeriod: null } : {}
    }
  }
}
